import {
  EBehavior,
  ICheckboxGroupsChangeModel,
  ICheckboxGroupsItemChangeModel,
  NestedCheckboxGroupsWithSearchDesktop,
  convertCheckboxGroupItemsToCheckboxGroup,
} from '@cian/nested-checkbox-groups';
import { Outside } from '@cian/ui-kit';
import { IconActionChevronDownSmall16 } from '@cian/ui-kit-design-tokens/icons';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AdvancedFilterRow } from 'shared/components/AdvancedFilter';
import { useFilterChangeCallback } from 'shared/hooks';
import { selectSpecialtyTypes } from 'shared/selectors/jsonQuery';
import { selectSpecialtyTypeOptionsRaw } from 'shared/selectors/filters/specialtyTypes/selectSpecialtyTypesOptions';

import styles from './SpecialtyTypesAdvancedContainer.css';

const PLACEHOLDER = 'Выберите назначение';

export const SpecialtyTypesAdvancedContainer: FC = () => {
  const items = useSelector(selectSpecialtyTypeOptionsRaw);
  const selectedIds = useSelector(selectSpecialtyTypes) ?? [];

  const onChange = useFilterChangeCallback();
  const [opened, setOpened] = useState(false);

  const groups = useMemo(
    () => [
      convertCheckboxGroupItemsToCheckboxGroup(
        items.map(item => ({ label: item.name, value: item.id })),
        '',
        'specialties',
      ),
    ],
    [items],
  );

  const buttonLabel = useMemo(() => {
    if (selectedIds.length === 0) {
      return PLACEHOLDER;
    }

    const labels = selectedIds.map(id => items.find(it => it.id === id)?.name).filter(Boolean) as string[];

    return labels.join(', ') || `Выбрано ${selectedIds.length}`;
  }, [items, selectedIds]);

  const apply = useCallback(
    (allItems: { value: number }[]) => {
      const ids = allItems.map(item => item.value);
      onChange({ action: 'setSpecialtyTypes', arguments: [ids.length > 0 ? ids : null] });
    },
    [onChange],
  );

  const handleNestedClick = useCallback(
    (model: ICheckboxGroupsItemChangeModel) => {
      apply(model.allItems);
    },
    [apply],
  );

  const handleTitleClick = useCallback(
    (model: ICheckboxGroupsChangeModel) => {
      apply(model.allItems);
    },
    [apply],
  );

  if (items.length === 0) {
    return null;
  }

  const isPlaceholder = selectedIds.length === 0;

  return (
    <AdvancedFilterRow label="Назначение">
      <Outside onOutside={() => setOpened(false)} active={opened}>
        <div className={styles['wrapper']}>
          <button type="button" className={styles['trigger']} onClick={() => setOpened(prev => !prev)}>
            <span className={isPlaceholder ? styles['trigger-placeholder'] : styles['trigger-label']}>
              {buttonLabel}
            </span>
            <span className={styles['trigger-arrow']}>
              <IconActionChevronDownSmall16 color="icon-main-default" />
            </span>
          </button>
          {opened && (
            <div className={styles['popup']}>
              <NestedCheckboxGroupsWithSearchDesktop
                behavior={EBehavior.Multiply}
                classNameContainer={styles['popup-content']}
                classNameNestedCheckboxGroups={styles['checkbox-groups']}
                groups={groups}
                values={selectedIds}
                onNestedCheckboxClick={handleNestedClick}
                onTitleCheckboxClick={handleTitleClick}
              />
            </div>
          )}
        </div>
      </Outside>
    </AdvancedFilterRow>
  );
};
